import axios from 'axios';
import { Business, Activity, ActivityForHomepage, MapBounds } from '@/types/homepage';
import { BusinessRegistrationForm } from '@/types/businessRegistration';

// Get the base URL from environment variables
const getBaseUrl = () => {
  if (import.meta.env.MODE === 'test') {
    return 'http://localhost:3000/api';
  }
  
  // For development and production
  if (typeof window !== 'undefined') {
    return import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
  }
  
  return 'http://localhost:3000/api';
};

const BASE_URL = getBaseUrl();

// Cache for custom adventures to prevent excessive API calls
const customAdventuresCache = new Map<string, { data: any, timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const fetchBusinessesInBounds = async (bounds: MapBounds): Promise<Business[]> => {
  // Skip degenerate bounds
  const lngSpan = bounds.northEast.longitude - bounds.southWest.longitude;
  if (lngSpan <= 0) {
    return [];
  }

  const boundsString = `${bounds.southWest.longitude},${bounds.southWest.latitude},${bounds.northEast.longitude},${bounds.northEast.latitude}`;

  try {
    const response = await axios.get(`${BASE_URL}/animal-activities/businesses-in-bounds`, {
      params: { bounds: boundsString }
    });
    return response.data;
  } catch (error: any) {
    // 404 means the backend found nothing for these bounds — treat as empty, not an error
    if (error?.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

const toBounds = (west: number, south: number, east: number, north: number): MapBounds => ({
  northEast: { latitude: north, longitude: east },
  southWest: { latitude: south, longitude: west }
});

/**
 * Loads every public operator. The catalogue is small, so the homepage loads it once
 * and filters by the map's view in the browser.
 */
export const fetchAllBusinesses = async (): Promise<Business[]> => {
  const world = await fetchBusinessesInBounds(toBounds(-180, -85, 180, 85));
  if (world.length > 0) return world;

  // Older API versions return nothing for spans past 180 degrees, so ask area by area
  const areas = [
    toBounds(-180, 0, -60, 85), toBounds(-60, 0, 60, 85), toBounds(60, 0, 180, 85),
    toBounds(-180, -85, -60, 0), toBounds(-60, -85, 60, 0), toBounds(60, -85, 180, 0)
  ];
  const results = await Promise.all(areas.map(fetchBusinessesInBounds));
  return [...new Map(results.flat().map(business => [business._id, business])).values()];
};

export async function fetchBusinessActivities(businessId: string): Promise<ActivityForHomepage[]> {
  try {
    const response = await axios.get(`${BASE_URL}/animal-activities/business/${businessId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching activities for business ${businessId}:`, error);
    return [];
  }
}

// This is the single source of truth for checking if a business has custom adventures.
// If you need this logic elsewhere, import from this file.
export async function hasCustomAdventures(businessId: string): Promise<boolean> {
  // Check cache first
  const cached = customAdventuresCache.get(businessId);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }

  try {
    const response = await axios.get(`${BASE_URL}/custom-adventures`, {
      params: { businessId },
      timeout: 3000 // 3 second timeout
    });
    
    // Handle both response formats: direct array or {success: true, data: []}
    const adventuresData = response.data.data || response.data;
    const hasAdventures = Array.isArray(adventuresData) && adventuresData.length > 0;
    
    // Cache the result
    customAdventuresCache.set(businessId, {
      data: hasAdventures,
      timestamp: Date.now()
    });
    
    return hasAdventures;
  } catch (error) {
    console.error(`Error checking custom adventures for business ${businessId}:`, error);
    return false;
  }
}

export async function checkEmailExists(email: string): Promise<{ exists: boolean; message: string }> {
  try {
    const response = await axios.get(`${BASE_URL}/auth/check-email`, {
      params: { email }
    });

    return {
      exists: response.data.exists,
      message: response.data.message
    };
  } catch (error: any) {
    console.error('Email check error:', error);
    
    // Handle specific error cases
    if (error.response?.status === 400) {
      throw new Error('Please provide a valid email address');
    } else if (error.response?.status === 429) {
      throw new Error('Too many requests. Please try again later');
    } else if (!error.response) {
      throw new Error('Network error. Please check your connection and try again');
    }
    
    throw new Error('Unable to verify email availability. Please try again');
  }
}

export async function registerBusiness(businessData: BusinessRegistrationForm['businessData']) {
  try {
    // First check if business exists
    try {
      const checkResponse = await axios.get(`${BASE_URL}/business/check-exists`, {
        params: {
          name: businessData.name,
          email: businessData.contact.email
        }
      });
      
      if (checkResponse.data.exists) {
        const error = new Error(checkResponse.data.message || 'A business with this name or email already exists');
        (error as any).code = 'BUSINESS_EXISTS';
        throw error;
      }
    } catch (checkError: any) {
      if (checkError.code === 'BUSINESS_EXISTS' || checkError.message?.includes('already exists')) {
        throw checkError;
      }
      console.warn('Business existence check failed:', checkError);
    }

    const formattedData = {
      ...businessData,
      experienceCategories: businessData.experiences || [],
      settings: {
        cancellationPolicy: {
          timeLimit: 24,
          refundPercentage: 80
        },
        bookingSettings: {
          minAdvanceTime: 24,
          maxAdvanceTime: 90,
          instantConfirmation: true
        },
        notificationPreferences: {
          email: true,
          sms: false,
          pushNotifications: false
        }
      },
      subscription: {
        plan: 'free',
        startDate: new Date(),
        endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
        status: 'active'
      }
    };

    try {
      const response = await axios.post(`${BASE_URL}/business`, formattedData);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else if (response.data._id) {
        return response.data;
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (axiosError: any) {
      const errorDetails = {
        status: axiosError.response?.status,
        statusText: axiosError.response?.statusText,
        data: axiosError.response?.data,
        message: axiosError.message
      };

      let errorMessage = 'Failed to create business profile';
      if (axiosError.response?.status === 400) {
        errorMessage = 'Invalid business data provided';
        if (axiosError.response.data?.message) {
          errorMessage += `: ${axiosError.response.data.message}`;
        }
      } else if (axiosError.response?.status === 401) {
        errorMessage = 'Authentication error. Please try logging in again.';
      } else if (axiosError.response?.status === 409) {
        errorMessage = axiosError.response.data?.message || 'A business with this information already exists';
        const error = new Error(errorMessage);
        (error as any).code = 'BUSINESS_EXISTS';
        throw error;
      } else if (axiosError.response?.status === 422) {
        errorMessage = 'Please check your business information and try again';
      } else if (!axiosError.response) {
        errorMessage = 'Network error. Please check your connection and try again';
      }

      const enhancedError = new Error(errorMessage);
      (enhancedError as any).details = errorDetails;
      (enhancedError as any).code = axiosError.response?.data?.code || 'BUSINESS_CREATION_ERROR';
      
      console.error('Business registration error:', {
        message: errorMessage,
        details: errorDetails
      });
      
      throw enhancedError;
    }
  } catch (error: any) {
    if ((error as any).details || error.message?.includes('BUSINESS_EXISTS') || error.code === 'BUSINESS_EXISTS') {
      throw error;
    }
    console.error('Unexpected error during business registration:', error);
    throw new Error('An unexpected error occurred while creating your business profile');
  }
}

export const registerBusinessUser = async (userData: {
  email: string;
  password: string;
  name: string;
  dateOfBirth: Date | string;
  nationality: string;
  role: string;
  businessId: string;
  sendVerificationEmail?: boolean;
}) => {
  try {
    // Check if email exists first
    const emailCheck = await checkEmailExists(userData.email);
    if (emailCheck.exists) {
      const error = new Error('This email is already registered');
      (error as any).code = 'EMAIL_EXISTS';
      throw error;
    }

    const formattedData = {
      email: userData.email,
      password: userData.password,
      name: userData.name,
      dateOfBirth: userData.dateOfBirth instanceof Date 
        ? userData.dateOfBirth.toISOString() 
        : new Date(userData.dateOfBirth).toISOString(),
      nationality: userData.nationality,
      role: 'business',
      businessId: userData.businessId,
      sendVerificationEmail: userData.sendVerificationEmail ?? true,
      preferences: {
        favoriteAnimals: [],
        preferredActivities: [],
        languagesSpoken: []
      }
    };

    const response = await axios.post(`${BASE_URL}/auth/register`, formattedData);
    return response;
  } catch (error: any) {
    // Enhanced error handling
    const errorDetails = {
      status: error.response?.status,
      code: error.response?.data?.code || error.code,
      message: error.response?.data?.message || error.message,
      data: error.response?.data
    };
    console.error('Business user registration error:', errorDetails);

    // If registration fails and it's not because of duplicate email
    if (!error.code?.includes('EMAIL_EXISTS')) {
      // Notify backend to rollback business creation
      try {
        await axios.delete(`${BASE_URL}/business/${userData.businessId}/rollback`);
      } catch (rollbackError) {
        console.error('Failed to rollback business creation:', rollbackError);
      }
    }

    // Enhance error object with additional context
    if (error.response?.data) {
      error.code = error.response.data.code;
      error.details = error.response.data;
    }
    throw error;
  }
};

export const processBusinessData = (business: any): Business => {
  // Ensure backward compatibility for existing businesses
  return {
    ...business,
    hasAnimalActivities: business.hasAnimalActivities || false,
    hasAdventureActivities: business.hasAdventureActivities || false,
    // ... rest of the existing processing ...
  };
};